import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Moon, Sun, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/common/hooks/theme/use-theme";
import { Theme, useUserSettingsQuery } from "@/common/api/services/userSettings";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useParams } from "react-router-dom";
import { UserDto } from "@/common/api/datasources/Api";
import { useState, useEffect, ReactNode } from "react";
import { DeleteAccountSection } from "./DeleteAccountSection";

interface ThemeOption {
  value: Theme;
  label: string;
  icon: ReactNode;
}

const DEFAULT_THEME: Theme = "system";

export const InterfaceSettings = () => {
  const { t } = useTranslation();
  const { setTheme } = useTheme();
  const { toast } = useToast();
  const { userUUID } = useParams();
  const queryClient = useQueryClient();
  const [userTheme, setUserTheme] = useState<Theme>(DEFAULT_THEME);

  const { settingsQuery, updateSettingsMutation } = useUserSettingsQuery();


  const themeOptions: ThemeOption[] = [
    {
      value: "light",
      label: t("settings.interface.theme.options.light"),
      icon: <Sun className="h-5 w-5 text-muted-foreground" />
    },
    {
      value: "dark",
      label: t("settings.interface.theme.options.dark"),
      icon: <Moon className="h-5 w-5 text-muted-foreground" />
    },
    {
      value: "system",
      label: t("settings.interface.theme.options.system"),
      icon: userTheme === "dark"
        ? <Moon className="h-5 w-5 text-muted-foreground" />
        : <Sun className="h-5 w-5 text-muted-foreground" />
    }
  ];

  const { data: settings, isLoading: isSettingsLoading } = useQuery({
    queryKey: ["user-settings", userUUID],
    queryFn: () => settingsQuery.queryFn(userUUID),
    enabled: !!userUUID,
  });

  const { data: currentUser } = useQuery<UserDto>({
    queryKey: ["profile"],
  });


  useEffect(() => {
    if (settings?.theme) {
      setUserTheme(settings.theme as Theme);
    }
  }, [settings?.theme]);

  // Мутація для оновлення налаштувань
  const { mutate: updateSettings, isPending: isUpdating } = useMutation({
    ...updateSettingsMutation,
    onSuccess: (data) => {
      handleSuccessUpdate(data.theme as Theme);
    },
    onError: () => {
      handleErrorUpdate();
    },
  });

  const handleSuccessUpdate = (theme: Theme) => {
    if (userUUID === currentUser?.userUUID) {
      setTheme(theme);
    }

    setUserTheme(theme);

    invalidateThemeCaches();

    toast({
      title: t("settings.interface.theme.success.title"),
      description: t("settings.interface.theme.success.description"),
    });
  };


  const handleErrorUpdate = () => {
    toast({
      variant: "destructive",
      title: t("settings.interface.theme.error.title"),
      description: t("settings.interface.theme.error.description"),
    });
  };

  const invalidateThemeCaches = () => {
    queryClient.invalidateQueries({ queryKey: ["user-settings"] });
    if (userUUID) {
      queryClient.invalidateQueries({ queryKey: ["user-settings", userUUID] });
    }
  };

  const handleThemeChange = (newTheme: string) => {
    if (!userUUID) return;

    setUserTheme(newTheme as Theme);

    updateSettings({
      userUUID,
      data: {
        theme: newTheme as Theme,
      },
    });
  };

  if (isSettingsLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-6">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const getCurrentThemeIcon = () => {
    return userTheme === "dark"
      ? <Moon className="h-5 w-5 text-muted-foreground" />
      : <Sun className="h-5 w-5 text-muted-foreground" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("settings.interface.title")}</CardTitle>
        <CardDescription>{t("settings.interface.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex items-center space-x-4">
            {getCurrentThemeIcon()}
            <div>
              <Label>{t("settings.interface.theme.title")}</Label>
              <p className="text-sm text-muted-foreground">
                {t("settings.interface.theme.description")}
              </p>
            </div>
          </div>
          <Select
            value={userTheme}
            onValueChange={handleThemeChange}
            disabled={isUpdating}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t("settings.interface.theme.title")} />
            </SelectTrigger>
            <SelectContent>
              {themeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
};
